import {
  ControlCode,
  ControlMonitorEndpointControlSummary,
  ControlCodes,
  PartnerEventCode,
  PartnerEvents,
  ValidPartnerEventCode,
  ValidControlCategory,
  ValidControlCategories,
} from "@theprelude/sdk";
import { assertNever } from "./assert-never";
import { getRFMDescription } from "./get-control-name";
import { isTruthy } from "./is-truthy";

export function isValidPartnerEvent(
  event: PartnerEventCode,
): event is ValidPartnerEventCode {
  return event !== PartnerEvents.INVALID;
}

export const getPartnerEventName = (
  event: PartnerEventCode,
  control: ControlCode = ControlCodes.NONE,
) => {
  switch (event) {
    case PartnerEvents.INVALID: {
      return "Unknown";
    }
    case PartnerEvents.MISSING_AV_POLICY: {
      return "Missing AV Policy";
    }
    case PartnerEvents.MISSING_EDR_POLICY: {
      return "Missing EDR Policy";
    }
    case PartnerEvents.MISSING_MFA: {
      return "Missing MFA";
    }
    case PartnerEvents.NO_ASSET_MANAGER:
    case PartnerEvents.USER_MISSING_ASSET_MANAGER: {
      return "No Endpoint Management";
    }
    case PartnerEvents.NO_EDR:
    case PartnerEvents.USER_MISSING_EDR: {
      return "No EDR";
    }
    case PartnerEvents.NO_VULN_MANAGER: {
      return "Missing Vulnerability Agent";
    }
    case PartnerEvents.USER_MISSING_VULN_MANAGER: {
      return "No Vulnerability Management";
    }
    case PartnerEvents.REDUCED_FUNCTIONALITY_MODE: {
      return getRFMDescription(control);
    }
    case PartnerEvents.MISCONFIGURED_POLICY_SETTING: {
      return "Misconfigured Policy Setting";
    }
    case PartnerEvents.MISSING_SCAN: {
      return "Missing Vulnerability Scan";
    }
    case PartnerEvents.OUT_OF_DATE_SCAN: {
      return "Out-of-Date Vulnerability Scan";
    }
    default: {
      return assertNever(event);
    }
  }
};

export type EndpointPartnerEventCode =
  | typeof PartnerEvents.NO_ASSET_MANAGER
  | typeof PartnerEvents.NO_EDR
  | typeof PartnerEvents.NO_VULN_MANAGER
  | typeof PartnerEvents.REDUCED_FUNCTIONALITY_MODE;

type DefensivePartnerEventCode =
  | typeof PartnerEvents.MISSING_AV_POLICY
  | typeof PartnerEvents.MISSING_EDR_POLICY
  | typeof PartnerEvents.REDUCED_FUNCTIONALITY_MODE;

type VulnerabilityPartnerEventCode =
  | typeof PartnerEvents.OUT_OF_DATE_SCAN
  | typeof PartnerEvents.MISSING_SCAN;

type IdentityProviderPartnerEventCode =
  | typeof PartnerEvents.MISSING_MFA
  | typeof PartnerEvents.USER_MISSING_ASSET_MANAGER
  | typeof PartnerEvents.USER_MISSING_EDR
  | typeof PartnerEvents.USER_MISSING_VULN_MANAGER;

export const getEndpointPartnerEventCodes = (
  category?: ValidControlCategory,
): EndpointPartnerEventCode[] => {
  const isAny = category === undefined;
  const isEDR = category === ValidControlCategories.XDR;
  const isEM = category === ValidControlCategories.ASSET_MANAGER;
  const isVM = category === ValidControlCategories.VULN_MANAGER;

  return [
    (isAny || isEDR || isEM || isVM) && PartnerEvents.NO_ASSET_MANAGER,
    (isAny || isEDR || isEM || isVM) && PartnerEvents.NO_EDR,
    (isAny || isEDR || isEM || isVM) && PartnerEvents.NO_VULN_MANAGER,
    (isAny || isEDR || isEM) && PartnerEvents.REDUCED_FUNCTIONALITY_MODE,
  ].filter(isTruthy);
};

export const getDefensivePartnerEventCodes = (
  endpointControl: ControlMonitorEndpointControlSummary | undefined,
): DefensivePartnerEventCode[] =>
  [
    (!endpointControl || endpointControl.no_av_policy !== null) &&
      PartnerEvents.MISSING_AV_POLICY,
    (!endpointControl || endpointControl.no_edr_policy !== null) &&
      PartnerEvents.MISSING_EDR_POLICY,
    (!endpointControl || endpointControl.reduced_functionality_mode !== null) &&
      PartnerEvents.REDUCED_FUNCTIONALITY_MODE,
  ].filter(isTruthy);

export const getVulnerabilityPartnerEventCodes = (
  endpointControl: ControlMonitorEndpointControlSummary | undefined,
): VulnerabilityPartnerEventCode[] =>
  [
    (!endpointControl || endpointControl.missing_scan_count !== null) &&
      PartnerEvents.MISSING_SCAN,
    (!endpointControl || endpointControl.out_of_date_scan_count !== null) &&
      PartnerEvents.OUT_OF_DATE_SCAN,
  ].filter(isTruthy);

export const identityProviderPartnerEventCodes: IdentityProviderPartnerEventCode[] =
  [
    PartnerEvents.MISSING_MFA,
    PartnerEvents.USER_MISSING_ASSET_MANAGER,
    PartnerEvents.USER_MISSING_EDR,
    PartnerEvents.USER_MISSING_VULN_MANAGER,
  ];
